import { searchCompanies, sortCompanies } from '../../utils/admin-utils'
import {
  AssignUserToCompanyAction,
  AssignUserToCompanySuccessAction,
  CreateCompanyAction,
  CreateCompanySuccessAction,
  DeleteCompanyAction,
  DeleteCompanyIntegrationAction,
  DeleteCompanyIntegrationSuccessAction,
  DeleteCompanySuccessAction,
  DeleteCompanyUserAction,
  DeleteCompanyUserSuccessAction,
  FetchCompaniesAction,
  FetchCompaniesSuccessAction,
  FetchCompanyIntegrationAction,
  FetchCompanyIntegrationSuccessAction,
  FetchCompanyUsersAction,
  FetchCompanyUsersSuccessAction,
  FetchUnassignedUsersAction,
  FetchUnassignedUsersSuccessAction,
  OpenEditCompanyDialogAction,
  SearchCompaniesAction,
  SetCompanyEditModeAction,
  UpdateCompanyAction,
  UpdateCompanyIntegrationAction,
  UpdateCompanyIntegrationSuccessAction,
  UpdateCompanySuccessAction
} from './admin-companies-actions'
import { createReducer, type UnknownAction } from '@reduxjs/toolkit'
import type BaseState from '../../interfaces/BaseState'
import {
  type AdminCompany,
  type AdminCompanyIntegration,
  type AdminCompanyWithoutUsers,
  type SelectedOrganization,
  type UnassignedUser
} from './admin-companies-types'
import { type User } from '../../interfaces/User'

export const getEmptyEditedOrganization = (): SelectedOrganization => {
  return {
    id: '',
    name: '',
    postalCode: '',
    streetAddress: '',
    town: '',
    vatId: '',
    newCompany: false
  }
}

/**
 * Admin companies state.
 */
export interface AdminState extends BaseState {
  companies: AdminCompany[]
  companyIntegrations: AdminCompanyIntegration[]
  searchedCompanies: AdminCompany[]
  userToRemove: User | null
  searchText: string
  unassignedUsers: UnassignedUser[]
  unassignedUsersLoaded: boolean
  editCompanyDialog: {
    editCompanyDialogOpen: boolean
    selectedOrganization: SelectedOrganization | null
    editMode: boolean
  }
}

export const initialAdminState: AdminState = {
  loading: 0,
  companies: [],
  companyIntegrations: [],
  searchedCompanies: [],
  userToRemove: null,
  searchText: '',
  unassignedUsers: [],
  unassignedUsersLoaded: false,
  editCompanyDialog: {
    editCompanyDialogOpen: false,
    selectedOrganization: null,
    editMode: false
  }
}

/**
 * Admin reducer.
 */
export const adminReducer = createReducer(initialAdminState, builder => {
  builder.addCase(AssignUserToCompanyAction, (state, _action) => {
    state.loading += 1
  }).addCase(AssignUserToCompanySuccessAction, (state, action) => {
    state.loading -= 1
    const company = state.companies.find(it => it.id === action.payload.companyId)
    if (company != null) {
      company.users.push(action.payload)
    }
    state.unassignedUsers = state.unassignedUsers.filter(it => it.id !== action.payload.id)
  }).addCase(CreateCompanyAction, (state, action) => {
    state.loading += 1
  }).addCase(CreateCompanySuccessAction, (state, action) => {
    const companyWithEmptyUsers = toCompanyWithEmptyUsers(action.payload)
    const companies = state.companies.concat(companyWithEmptyUsers)
    state.loading -= 1
    state.companies = sortCompanies(companies)
    state.searchedCompanies = searchCompanies(companies, state.searchText)

    if (state.editCompanyDialog.selectedOrganization?.id === action.payload.id) {
      state.editCompanyDialog.selectedOrganization = {
        ...action.payload,
        newCompany: false
      }
    }
  }).addCase(DeleteCompanyAction, (state, action) => {
    state.loading += 1
  }).addCase(DeleteCompanySuccessAction, (state, action) => {
    state.loading -= 1
    state.companies = state.companies.filter(it => it.id !== action.payload.companyId)
    state.searchedCompanies = searchCompanies(state.companies, state.searchText)
    if (state.editCompanyDialog.selectedOrganization?.id === action.payload.companyId) {
      state.editCompanyDialog.editMode = false
      state.editCompanyDialog.editCompanyDialogOpen = false
      state.editCompanyDialog.selectedOrganization = null
    }
  }).addCase(DeleteCompanyUserAction, (state, action) => {
    state.loading += 1
  }).addCase(DeleteCompanyUserSuccessAction, (state, action) => {
    state.loading -= 1
    const company = state.companies.find(it => it.id === action.payload.companyId)
    if (company != null) {
      company.users = company.users.filter(it => it.id !== action.payload.userId)
    }
  }).addCase(DeleteCompanyIntegrationAction, (state, _action) => {
    state.loading += 1
  }).addCase(DeleteCompanyIntegrationSuccessAction, (state, action) => {
    state.loading -= 1
    state.companyIntegrations = state.companyIntegrations.filter(it => it.companyId !== action.payload.companyId)
  }).addCase(FetchCompaniesAction, (state, _action) => {
    state.loading += 1
  }).addCase(FetchCompaniesSuccessAction, (state, action) => {
    const companiesWithEmptyUsers = toCompaniesWithEmptyUsers(action.payload.companies)

    state.loading -= 1
    state.companies = sortCompanies(companiesWithEmptyUsers)
    state.searchedCompanies = searchCompanies(companiesWithEmptyUsers, state.searchText)
  }).addCase(FetchCompanyIntegrationAction, (state, _action) => {
    state.loading += 1
  }).addCase(FetchCompanyIntegrationSuccessAction, (state, action) => {
    state.loading -= 1
    state.companyIntegrations = state.companyIntegrations.filter(it => it.companyId !== action.payload.companyId)
    state.companyIntegrations.push(action.payload)
  }).addCase(FetchCompanyUsersAction, (state, _action) => {
    state.loading += 1
  }).addCase(FetchCompanyUsersSuccessAction, (state, action) => {
    state.loading -= 1
    const company = state.companies.find(it => it.id === action.payload.companyId)
    if (company != null) {
      company.users = action.payload.users
    }
  }).addCase(FetchUnassignedUsersAction, (state, _action) => {
    state.loading += 1
  }).addCase(FetchUnassignedUsersSuccessAction, (state, action) => {
    state.loading -= 1
    state.unassignedUsers = action.payload.users
    state.unassignedUsersLoaded = true
  }).addCase(SearchCompaniesAction, (state, action) => {
    state.searchText = action.payload.searchText
    state.searchedCompanies = searchCompanies(action.payload.searchCompanies, action.payload.searchText)
  }).addCase(UpdateCompanyIntegrationAction, (state, action) => {
    state.loading += 1
  }).addCase(UpdateCompanyIntegrationSuccessAction, (state, action) => {
    state.loading -= 1
    state.companyIntegrations = state.companyIntegrations.filter(it => it.companyId !== action.payload.companyId)
    state.companyIntegrations.push(action.payload)
  }).addCase(UpdateCompanyAction, (state, action) => {
    state.loading += 1
  }).addCase(UpdateCompanySuccessAction, (state, action) => {
    const companyWithEmptyUsers = toCompanyWithEmptyUsers(action.payload)
    const companies = state.companies.filter(it => it.id !== companyWithEmptyUsers.id).concat(companyWithEmptyUsers)

    state.loading -= 1
    state.companies = sortCompanies(companies)
    state.searchedCompanies = searchCompanies(companies, state.searchText)
  }).addCase(OpenEditCompanyDialogAction, (state, action) => {
    state.editCompanyDialog.editCompanyDialogOpen = true
    state.editCompanyDialog.editMode = action.payload.startInEditMode ?? state.editCompanyDialog.editMode
    state.editCompanyDialog.selectedOrganization = action.payload.company
  }).addCase(SetCompanyEditModeAction, (state, action) => {
    state.editCompanyDialog.editMode = action.payload.editMode
  }).addMatcher(isFailureAction, (state, _action) => {
    // Matcher for actions that just decreases loading count (and has no 'case' defined above)
    state.loading -= 1
  })
})

function isFailureAction(action: UnknownAction): boolean {
  return typeof action.type === 'string' && action.type.startsWith('companies/') && (action.type.endsWith('/failure') || action.type.endsWith('Failure'))
}

function toCompaniesWithEmptyUsers(companies: AdminCompanyWithoutUsers[]): AdminCompany[] {
  return companies.map(toCompanyWithEmptyUsers)
}

function toCompanyWithEmptyUsers(company: AdminCompanyWithoutUsers): AdminCompany {
  return {
    ...company,
    users: []
  }
}
